import type { CreateTodoBody } from '@kanto/shared';
import { useState, type FormEvent, type ReactElement } from 'react';
import { useT } from '../lib/i18n';
import { TagInput } from './TagInput';

/**
 * The field at the top of the list, and the only way a todo is written.
 *
 * One line by default. The tags and a due date are shown once there is something
 * to attach them to — a composer that asks for three things before it accepts one
 * is a composer people stop using for the todo they had in mind five seconds ago.
 *
 * The form stays on screen in every state of the list, including the empty one:
 * the first thing to do on an empty list is add something to it.
 */
export function TodoComposer({
  onCreate,
  pending,
}: {
  onCreate: (body: CreateTodoBody) => void;
  pending: boolean;
}): ReactElement {
  const t = useT();
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [dueOn, setDueOn] = useState('');

  const ready = title.trim().length > 0;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!ready) return;

    onCreate({ title: title.trim(), tags, dueOn: dueOn || null });

    setTitle('');
    setTags([]);
    setDueOn('');
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-line bg-surface p-2">
      <div className="flex items-center gap-2">
        <svg
          viewBox="0 0 24 24"
          className="ml-1.5 size-5 shrink-0 text-ink-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <input
          type="text"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('todos.add')}
          aria-label={t('todos.add')}
          disabled={pending}
          className="min-h-11 w-full bg-transparent text-base outline-none placeholder:text-ink-400"
        />
        {/* The button appears with something to submit rather than sitting there
            disabled: a control that cannot be pressed still has to be read. */}
        {ready && (
          <button
            type="submit"
            disabled={pending}
            className="min-h-9 shrink-0 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim disabled:opacity-60"
          >
            {t('todos.addAction')}
          </button>
        )}
      </div>

      {ready && (
        <div className="mt-2 flex flex-wrap items-start gap-2 border-t border-line pt-2 pl-1.5">
          <TagInput tags={tags} onChange={setTags} disabled={pending} />
          <input
            type="date"
            value={dueOn}
            onChange={(event) => setDueOn(event.target.value)}
            aria-label={t('todos.due')}
            className="min-h-9 rounded-lg border border-line bg-paper px-2.5 text-sm text-ink-500 outline-none focus:border-accent"
          />
        </div>
      )}
    </form>
  );
}
